"use client"
import styled from "styled-components"
import { motion } from "framer-motion"
import { Download as DownloadIcon, History as HistoryIcon, Search as SearchIcon } from "@mui/icons-material"
import { format } from "date-fns"
import { API_BASE } from "../config/api"

const HistoryContainer = styled(motion.div)`
  background-color: var(--input-bg);
  border-radius: 8px;
  padding: 30px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  border: 1px solid var(--border);
`

const HistoryTitle = styled.h2`
  margin-bottom: 24px;
  color: var(--primary-text);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 10px;
`

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
`

const HistoryItem = styled(motion.div)`
  background-color: rgba(13, 12, 29, 0.6);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid var(--border);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--highlight);
    background-color: rgba(13, 12, 29, 0.8);
  }
`

const HistoryQuery = styled.h3`
  color: var(--primary-text);
  font-size: 1.1rem;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const HistoryTimestamp = styled.div`
  color: var(--secondary-text);
  font-size: 0.9rem;
  margin-bottom: 16px;
`

const HistoryOutput = styled.div`
  background-color: rgba(13, 12, 29, 0.8);
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 16px;
  font-family: monospace;
  font-size: 0.9rem;
  color: var(--secondary-text);
  max-height: 150px;
  overflow-y: auto;
  white-space: pre-wrap;
`

const HistoryActions = styled.div`
  display: flex;
  justify-content: flex-end;
`

const DownloadButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--accent-blue);
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--highlight);
  }
`

const NoHistory = styled.div`
  color: var(--secondary-text);
  text-align: center;
  padding: 40px 0;
  font-size: 1.1rem;
`

const History = ({ historyItems }) => {
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: i * 0.1,
      },
    }),
    hover: {
      y: -5,
      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
    },
  }

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  }

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), "MMM d, yyyy h:mm a")
    } catch (error) {
      return timestamp
    }
  }

  return (
    <HistoryContainer variants={containerVariants} initial="hidden" animate="visible">
      <HistoryTitle>
        <HistoryIcon />
        Query History
      </HistoryTitle>

      {historyItems && historyItems.length > 0 ? (
        <HistoryList>
          {historyItems.map((item, index) => (
            <HistoryItem
              key={item.id}
              custom={index}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              whileHover="hover"
            >
              <HistoryQuery>
                <SearchIcon fontSize="small" />
                {item.question}
              </HistoryQuery>

              <HistoryTimestamp>{formatTimestamp(item.timestamp)}</HistoryTimestamp>

              {item.output && (
                <HistoryOutput>
                  {item.output.length > 300 ? `${item.output.substring(0, 300)}...` : item.output}
                </HistoryOutput>
              )}

              {item.docx_path && (
                <HistoryActions>
                  <DownloadButton
                    href={`${API_BASE}/download/${item.docx_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <DownloadIcon fontSize="small" />
                    Download Report
                  </DownloadButton>
                </HistoryActions>
              )}
            </HistoryItem>
          ))}
        </HistoryList>
      ) : (
        <NoHistory>No query history found</NoHistory>
      )}
    </HistoryContainer>
  )
}

export default History

